/**
 * Weighted quote scoring for side-by-side comparison — advisory only.
 *
 * Scoring formula (v2):
 *   baseScore = cost × W_cost + codFit × W_cod + reliability × W_rel + risk × W_risk
 *              - compliancePenalty
 *   v2 adjustments:
 *     + confidenceMultiplier  (V=+0.05, R=0, C=-0.10)
 *     - citationPenalty       (block: -0.08 each, warn: -0.03 each, capped -0.15)
 *     + downstreamBonus       (0.02 per downstream action, capped +0.10)
 *     + phaseAlignmentBonus   (on-time: +0.05, early: +0.03, late: -0.08)
 */
import type { QuoteComparisonRow } from './buildQuoteComparison';
import type { QuoteConfidence } from './supplyChain/quoteConfidence';
import type { QuoteCitationViolation } from './audit/citationIntegrityVerifier';
import type { DownstreamAction } from './audit/recommendationGraphBuilder';
import type { QuotePhaseImpactResult } from './supplyChain/quotePhaseImpact';

export interface QuoteComparisonWeights {
  cost: number;
  codFit: number;
  reliability: number;
  risk: number;
}

export const DEFAULT_QUOTE_COMPARISON_WEIGHTS: QuoteComparisonWeights = {
  cost: 0.35,
  codFit: 0.30,
  reliability: 0.20,
  risk: 0.15,
};

export interface EnrichedQuoteSignals {
  confidence?: QuoteConfidence;
  citationViolations?: QuoteCitationViolation[];
  downstreamActions?: DownstreamAction[];
  phaseImpact?: QuotePhaseImpactResult;
}

export interface ScoringDriver {
  label: string;
  impact: number;
}

export interface ScoredQuoteResult {
  quoteId: string;
  score: number;
  drivers: ScoringDriver[];
}

const TIER_SCORE: Record<string, number> = {
  'tier-1': 1,
  'tier-2': 0.6,
  'tier-3': 0.3,
};

const RISK_SCORE: Record<string, number> = {
  low: 1,
  medium: 0.65,
  high: 0.35,
  critical: 0,
};

const CONFIDENCE_MULTIPLIER: Record<string, number> = {
  V: 0.05,
  R: 0,
  C: -0.10,
};

const CITATION_PENALTY_BLOCK = 0.08;
const CITATION_PENALTY_WARN = 0.03;
const CITATION_PENALTY_CAP = 0.15;
const DOWNSTREAM_BONUS_RATE = 0.02;
const DOWNSTREAM_BONUS_CAP = 0.10;
const PHASE_ALIGNMENT_ON_TIME = 0.05;
const PHASE_ALIGNMENT_EARLY = 0.03;
const PHASE_ALIGNMENT_LATE = -0.08;
const CERT_CONFIRMED_BONUS = 0.02;
const CERT_UNCONFIRMED_PENALTY = 0.06;
const SLOT_STATUS_ADJUSTMENT: Record<string, number> = {
  reserved: 0.05,
  available: 0.03,
  limited: -0.02,
  unavailable: -0.08,
  unknown: -0.01,
};
const CAPACITY_STATUS_ADJUSTMENT: Record<string, number> = {
  reserved: 0.04,
  available: 0.03,
  limited: -0.03,
  unavailable: -0.08,
  unknown: -0.01,
};

function normalizeHigherIsBetter(value: number, min: number, max: number): number {
  if (max === min) return 1;
  return 1 - (value - min) / (max - min);
}

export function scoreQuoteForBomItem(
  row: QuoteComparisonRow,
  allRows: QuoteComparisonRow[],
  weights: QuoteComparisonWeights = DEFAULT_QUOTE_COMPARISON_WEIGHTS,
  signals?: EnrichedQuoteSignals,
): number {
  const costs = allRows.map(r => r.totalCostUsd);
  const costNorm = normalizeHigherIsBetter(row.totalCostUsd, Math.min(...costs), Math.max(...costs));

  const leads = allRows.map(r => r.quote.leadTimeMonths);
  const leadNorm = normalizeHigherIsBetter(row.quote.leadTimeMonths, Math.min(...leads), Math.max(...leads));

  let codNorm = leadNorm;
  const slips = allRows.map(r => r.codSlipMonths).filter((s): s is number => s !== null);
  if (slips.length > 0 && row.codSlipMonths !== null) {
    codNorm = normalizeHigherIsBetter(row.codSlipMonths, Math.min(...slips), Math.max(...slips));
  }

  const relNorm = TIER_SCORE[row.supplier.reliabilityTier ?? 'tier-3'] ?? 0.3;
  const riskNorm = RISK_SCORE[row.risk.overallTier] ?? 0.5;
  const compliancePenalty = row.meetsCompliance ? 0 : 0.15;

  let baseScore =
    weights.cost * costNorm
    + weights.codFit * codNorm
    + weights.reliability * relNorm
    + weights.risk * riskNorm
    - compliancePenalty;

  if (signals?.confidence) {
    baseScore += CONFIDENCE_MULTIPLIER[signals.confidence.tag] ?? 0;
  }

  if (signals?.citationViolations) {
    const blocks = signals.citationViolations.filter(v => v.severity === 'block').length;
    const warns = signals.citationViolations.filter(v => v.severity === 'warn').length;
    const penalty = Math.min(
      blocks * CITATION_PENALTY_BLOCK + warns * CITATION_PENALTY_WARN,
      CITATION_PENALTY_CAP,
    );
    baseScore -= penalty;
  }

  if (signals?.downstreamActions && signals.downstreamActions.length > 0) {
    const bonus = Math.min(
      signals.downstreamActions.length * DOWNSTREAM_BONUS_RATE,
      DOWNSTREAM_BONUS_CAP,
    );
    baseScore += bonus;
  }

  if (signals?.phaseImpact?.available) {
    const alignment = signals.phaseImpact.impact.deliveryAlignment;
    if (alignment === 'on-time') baseScore += PHASE_ALIGNMENT_ON_TIME;
    else if (alignment === 'early') baseScore += PHASE_ALIGNMENT_EARLY;
    else baseScore += PHASE_ALIGNMENT_LATE;
  }

  if (row.quote.certificationComplianceConfirmed === true) {
    baseScore += CERT_CONFIRMED_BONUS;
  } else if (row.quote.certificationComplianceConfirmed === false) {
    baseScore -= CERT_UNCONFIRMED_PENALTY;
  }

  if (row.quote.productionSlotStatus) {
    baseScore += SLOT_STATUS_ADJUSTMENT[row.quote.productionSlotStatus] ?? 0;
  }
  if (row.quote.capacityAvailabilityStatus) {
    baseScore += CAPACITY_STATUS_ADJUSTMENT[row.quote.capacityAvailabilityStatus] ?? 0;
  }

  if (row.quote.validityDays != null && Number.isFinite(row.quote.validityDays)) {
    if (row.quote.validityDays >= 60) baseScore += 0.02;
    else if (row.quote.validityDays < 30) baseScore -= 0.03;
  }

  if (row.quote.depositAmountUsd != null && Number.isFinite(row.quote.depositAmountUsd) && row.totalCostUsd > 0) {
    const depositShare = row.quote.depositAmountUsd / row.totalCostUsd;
    if (depositShare > 0.3) baseScore -= 0.05;
    else if (depositShare > 0.15) baseScore -= 0.02;
  }

  return baseScore;
}

export function scoreQuoteWithDrivers(
  row: QuoteComparisonRow,
  allRows: QuoteComparisonRow[],
  weights: QuoteComparisonWeights = DEFAULT_QUOTE_COMPARISON_WEIGHTS,
  signals?: EnrichedQuoteSignals,
): ScoredQuoteResult {
  const score = scoreQuoteForBomItem(row, allRows, weights, signals);
  const drivers: ScoringDriver[] = [];

  if (signals?.confidence) {
    const tag = signals.confidence.tag;
    const impact = CONFIDENCE_MULTIPLIER[tag] ?? 0;
    if (tag === 'V') drivers.push({ label: 'Verified evidence (V tag)', impact });
    else if (tag === 'C') drivers.push({ label: 'Conditional evidence — needs verification', impact });
  }

  if (signals?.phaseImpact?.available) {
    const { deliveryAlignment, monthsAheadOrBehind, phaseLabel } = signals.phaseImpact.impact;
    if (deliveryAlignment === 'on-time') {
      drivers.push({ label: `${phaseLabel} on-time delivery`, impact: PHASE_ALIGNMENT_ON_TIME });
    } else if (deliveryAlignment === 'late') {
      drivers.push({ label: `${monthsAheadOrBehind}mo late for ${phaseLabel}`, impact: PHASE_ALIGNMENT_LATE });
    } else {
      drivers.push({ label: `${monthsAheadOrBehind}mo early for ${phaseLabel}`, impact: PHASE_ALIGNMENT_EARLY });
    }
  }

  if (signals?.downstreamActions && signals.downstreamActions.length > 0) {
    const n = signals.downstreamActions.length;
    const bonus = Math.min(n * DOWNSTREAM_BONUS_RATE, DOWNSTREAM_BONUS_CAP);
    drivers.push({ label: `Unblocks ${n} downstream construction milestones`, impact: bonus });
  }

  if (signals?.citationViolations && signals.citationViolations.length > 0) {
    const blocks = signals.citationViolations.filter(v => v.severity === 'block').length;
    const warns = signals.citationViolations.filter(v => v.severity === 'warn').length;
    const penalty = Math.min(
      blocks * CITATION_PENALTY_BLOCK + warns * CITATION_PENALTY_WARN,
      CITATION_PENALTY_CAP,
    );
    if (penalty > 0) {
      drivers.push({ label: `${blocks + warns} unverified claims`, impact: -penalty });
    }
  }

  if (row.quote.certificationComplianceConfirmed === false) {
    drivers.push({ label: 'Certification compliance not confirmed', impact: -CERT_UNCONFIRMED_PENALTY });
  } else if (row.quote.certificationComplianceConfirmed === true) {
    drivers.push({ label: 'Certification compliance confirmed', impact: CERT_CONFIRMED_BONUS });
  }

  if (row.quote.productionSlotStatus && row.quote.productionSlotStatus !== 'unknown') {
    drivers.push({
      label: `Production slot ${row.quote.productionSlotStatus}`,
      impact: SLOT_STATUS_ADJUSTMENT[row.quote.productionSlotStatus] ?? 0,
    });
  }
  if (row.quote.capacityAvailabilityStatus && row.quote.capacityAvailabilityStatus !== 'unknown') {
    drivers.push({
      label: `Capacity ${row.quote.capacityAvailabilityStatus}`,
      impact: CAPACITY_STATUS_ADJUSTMENT[row.quote.capacityAvailabilityStatus] ?? 0,
    });
  }

  if (row.quote.validityDays != null && Number.isFinite(row.quote.validityDays)) {
    if (row.quote.validityDays >= 60) {
      drivers.push({ label: `Long quote validity (${row.quote.validityDays} days)`, impact: 0.02 });
    } else if (row.quote.validityDays < 30) {
      drivers.push({ label: `Short quote validity (${row.quote.validityDays} days)`, impact: -0.03 });
    }
  }

  if (row.quote.depositAmountUsd != null && Number.isFinite(row.quote.depositAmountUsd) && row.totalCostUsd > 0) {
    const depositShare = row.quote.depositAmountUsd / row.totalCostUsd;
    if (depositShare > 0.3) {
      drivers.push({ label: 'High deposit burden', impact: -0.05 });
    } else if (depositShare > 0.15) {
      drivers.push({ label: 'Moderate deposit burden', impact: -0.02 });
    }
  }

  const costs = allRows.map(r => r.totalCostUsd);
  const costRank = [...costs].sort((a, b) => a - b).indexOf(row.totalCostUsd);
  if (costRank === 0 && allRows.length > 1) {
    drivers.push({ label: 'Lowest cost', impact: weights.cost });
  }

  drivers.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));

  return { quoteId: row.quote.id, score, drivers: drivers.slice(0, 3) };
}

export function pickRecommendedQuoteId(
  rows: QuoteComparisonRow[],
  weights?: QuoteComparisonWeights,
  signalsByQuoteId?: Map<string, EnrichedQuoteSignals>,
): string | undefined {
  if (rows.length < 2) return undefined;
  let bestId: string | undefined;
  let bestScore = -Infinity;
  for (const row of rows) {
    const signals = signalsByQuoteId?.get(row.quote.id);
    const score = scoreQuoteForBomItem(row, rows, weights, signals);
    if (score > bestScore) {
      bestScore = score;
      bestId = row.quote.id;
    }
  }
  return bestId;
}

